import { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, Row, Col, Select, Alert } from 'antd';
import CustomSelectStyles from 'components/ui/CustomSelect/CustomSelect.module.css';
import { Modal } from 'components/ui/Modal';
import clsx from 'clsx';
import MainStyles from 'assets/styles/main.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { Spinner } from '../../../../components/Spinner';
import {
  ROUTES,
  STATUS_LOADING,
  STATUS_NOT_REQUESTED,
} from '../../../../const';
import {
  COMPANY_SETTINGS_REWARDS,
  COMPANY_SETTINGS_REWARDS_DETAIL,
} from '../../../../const/translations/SalaryCompanySettings';
import { selectUserCompanyId } from '../../../../modules/loggedUserInfo/selectors';
import {
  addRewardToFamily,
  getCompanyRewards,
  getRewardsFamily,
  removeRewardFromFamily,
  setRewardFamilyMessage,
  setRewardsFamily,
} from '../../../../modules/SalaryModule/Rewards/actions';
import {
  selectAvailableRewardsOptions,
  selectCompanyRewardsStatus,
  selectCompanyUnaddedRewards,
  selectFamilyData,
  selectFamilyMessage,
  selectFamilyRewardsMapped,
} from '../../../../modules/SalaryModule/Rewards/selectors';
import { getRewardItemRemoveModalBody } from '../../../../modules/SalaryModule/Rewards/utils';
import Styles from './CompanySettingsManageRewardItemPage.module.css';
import { ManageRewardFamilyTitleForm } from './ManageRewardFamilyTitle';
import { EditableRewardsTable } from './EditableRewardsTable';

const { Paragraph, Text } = Typography;

let timeout;

export const CSManageRewardFamily = () => {
  const companyId = useSelector(selectUserCompanyId);
  const familyData = useSelector(selectFamilyData);
  const familyRewards = useSelector(selectFamilyRewardsMapped);
  const familyMessage = useSelector(selectFamilyMessage);
  const unaddedRewards = useSelector(selectCompanyUnaddedRewards);
  const unaddedRewardsOptions = useSelector(selectAvailableRewardsOptions);
  const dispatch = useDispatch();
  const [modalData, setModalData] = useState({
    title: '',
    text: '',
    isError: false,
  });
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [selectOpen, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();
  const loadingStatus = useSelector(selectCompanyRewardsStatus);
  const { id } = useParams();
  const rewardsSelectRef = useRef(null);

  const showRemoveRewardsItemModal = useCallback((e) => {
    const { id: rewardId, removable } = e.currentTarget.dataset;
    setModalData(getRewardItemRemoveModalBody(!!removable, rewardId));
    setIsVisibleModal(true);
  }, []);

  const closeRemoveModal = useCallback(() => {
    setIsVisibleModal(false);
  }, []);

  const submitRemoveModal = useCallback(() => {
    if (!modalData.isError && modalData.rewardId) {
      dispatch(
        removeRewardFromFamily({
          companyId,
          companyRewardId: modalData.rewardId,
          familyId: familyData.id,
        })
      );
    }
    setIsVisibleModal(false);
  }, [companyId, dispatch, familyData, modalData]);

  const handleAddReward = useCallback(
    (rewardId) => {
      const reward = unaddedRewards.find(({ id: rewId }) => rewId === rewardId);

      if (reward && familyData) {
        dispatch(
          addRewardToFamily({
            companyId,
            data: {
              rewardId,
              rewardFamilyId: familyData.id,
              customName: reward.name,
              customType: reward.type,
            },
          })
        );
        setSearchValue('');
        if (rewardsSelectRef.current) {
          rewardsSelectRef.current.blur();
        }
      } else {
        dispatch(
          setRewardFamilyMessage({
            type: 'error',
            text: COMPANY_SETTINGS_REWARDS_DETAIL.ERROR_MESSAGE,
          })
        );
      }
      setOpen(false);
    },
    [dispatch, unaddedRewards, companyId, familyData]
  );

  const handleSearch = useCallback((value) => {
    setSearchValue(value);
    if (value.length >= 3) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setOpen(false);
    }, 200);
  }, []);

  const handleFocus = useCallback(() => {
    if (searchValue.length >= 3) {
      setOpen(true);
    }
  }, [searchValue]);

  const filterOption = useCallback((input, option) => {
    if (!option || !option.label) return false;
    return option.label.toLowerCase().includes(input.toLowerCase());
  }, []);

  const filteredOptions = useCallback(() => {
    if (!searchValue || searchValue.length < 3) {
      return unaddedRewardsOptions;
    }
    return unaddedRewardsOptions.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [unaddedRewardsOptions, searchValue]);

  console.log(unaddedRewardsOptions);

  useEffect(() => {
    if (familyMessage) {
      timeout = setTimeout(() => {
        dispatch(setRewardFamilyMessage(null));
      }, 3000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [dispatch, familyMessage]);

  useEffect(
    () => () => {
      dispatch(setRewardFamilyMessage(null));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!currentCompany) {
      setCurrentCompany(companyId);
      return;
    }
    if (currentCompany !== companyId) {
      navigate(`${ROUTES.SALARIES_COMPANY_SETTINGS}?tab=3`);
      return;
    }
    if (id && companyId) {
      dispatch(
        getRewardsFamily({
          companyId,
          familyId: id,
        })
      );
    }
    if (companyId) {
      dispatch(getCompanyRewards({ companyId }));
    }
  }, [currentCompany, navigate, companyId, dispatch, id]);

  useEffect(
    () => () => {
      dispatch(setRewardsFamily(null));
    },
    [dispatch]
  );

  if ([STATUS_NOT_REQUESTED, STATUS_LOADING].includes(loadingStatus)) {
    return <Spinner />;
  }

  return (
    <div className={Styles.container}>
      <ManageRewardFamilyTitleForm
        companyId={companyId}
        familyData={familyData}
      />

      {familyMessage && (
        <Row type="flex" align="middle" className={Styles.row}>
          <Col xs={24} sm={24}>
            <Alert
              message={familyMessage.text}
              type={familyMessage.type}
              showIcon
              closable
              onClose={() => dispatch(setRewardFamilyMessage(null))}
              style={{ marginBottom: 16 }}
            />
          </Col>
        </Row>
      )}

      <Row type="flex" align="middle" className={Styles.row}>
        <Col xs={24} sm={24} lg={4}>
          <Text
            className={clsx(
              MainStyles.heading,
              MainStyles.formLabel,
              Styles.label
            )}
          >
            {COMPANY_SETTINGS_REWARDS.SELECT_TITLE}
          </Text>
        </Col>
        <Col xs={24} sm={24} lg={8}>
          <Select
            size="large"
            showSearch
            placeholder={
              COMPANY_SETTINGS_REWARDS.AUTOCOMPLETE_SELECT_PLACEHOLDER
            }
            className={clsx(
              CustomSelectStyles.select,
              CustomSelectStyles.showSearch,
              Styles.select
            )}
            classNames={{ popup: { root: CustomSelectStyles.dropdown } }}
            value={null}
            open={selectOpen}
            onChange={handleAddReward}
            onSearch={handleSearch}
            onBlur={handleBlur}
            onFocus={handleFocus}
            filterOption={filterOption}
            searchValue={searchValue}
            ref={rewardsSelectRef}
            optionFilterProp="label"
            notFoundContent={
              searchValue.length < 3
                ? 'Entrez au moins 3 caractères pour la recherche'
                : 'Aucun résultat trouvé'
            }
            popupMatchSelectWidth={false}
            style={{ width: '100%' }}
          >
            {filteredOptions().map(({ value, label }) => (
              <Select.Option key={value} value={value} label={label}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Text
        className={clsx(MainStyles.heading, MainStyles.formLabel, Styles.label)}
      >
        {COMPANY_SETTINGS_REWARDS.LIST_TITLE} :
      </Text>

      <EditableRewardsTable
        companyId={companyId}
        rewardsList={familyRewards}
        onRemoveRewardsItem={showRemoveRewardsItemModal}
      />

      <Modal
        fullMobileScreen={false}
        isVisible={isVisibleModal}
        width="384px"
        title={modalData.title}
        okText={COMPANY_SETTINGS_REWARDS.CONFIRM_TEXT}
        onClose={closeRemoveModal}
        isError={modalData.isError}
        onSubmit={submitRemoveModal}
        closable={false}
        isSmallModal
      >
        <Paragraph>
          {modalData.text}
          {modalData.isError && (
            <>
              <a
                href={ROUTES.SALARIES_REWARDS}
                target="_blank"
                rel="noopener noreferrer"
                className={Styles.link}
              >
                {COMPANY_SETTINGS_REWARDS_DETAIL.REMOVE_CANCEL_LINK}
              </a>
              {modalData.text2}
            </>
          )}
        </Paragraph>
      </Modal>
    </div>
  );
};
